import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatCostUsd } from '../../utils/formatCost';
import {
  enrichSessionWithCost,
  formatCostBreakdownDescription,
} from '../../utils/sessionCostEstimate';
import { getApiBase } from '../../lib/apiBase';
import { getDashboardAuthHeaders } from '../../lib/dashboardSession';
import { useDataTablesAssets, getJQuery } from '../../hooks/useDataTablesAssets';
import { normalizeDashboardMessages } from '../../lib/dashboardMessages';
import { formatDate } from './formatters';
import PaginationBar from './PaginationBar';

function escapeHtml(text) {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildDetailHtml(session, messages, loading) {
  const enriched = enrichSessionWithCost(session);
  const list = normalizeDashboardMessages(messages ?? session?.messages ?? []);
  const parts = [];
  const breakdownLine = formatCostBreakdownDescription(enriched.costBreakdown);
  if (breakdownLine) {
    parts.push(
      `<p class="text-muted text-sm mb-3 border-bottom pb-2"><strong>Cost breakdown:</strong> ${escapeHtml(breakdownLine)}</p>`
    );
  }
  if (loading) {
    parts.push('<p class="text-muted text-sm"><i class="fas fa-spinner fa-spin mr-1"></i> Loading messages…</p>');
  } else if (!list.length) {
    const count = Number(session?.messageCount) || 0;
    parts.push(
      `<p class="text-muted text-sm mb-0">${
        count > 0
          ? 'Transcript rows were never saved (often after a failed write). Use Sync from Sheet in the header, or run npm run import-sheets. New avatar chats are saved safely after the latest server update.'
          : 'No messages for this session.'
      }</p>`
    );
  } else {
    // Never fall back to avatarName for a user turn — that prints the bot's name
    // ("Afterlife Bot") where a person belongs.
    const userLabel = session?.userEmail || 'User';
    list.forEach((m) => {
      const role = m.role === 'user' ? userLabel : 'Mr. Berwind';
      const cls = m.role === 'user' ? 'message-user' : '';
      parts.push(
        `<div class="message-bubble ${cls}"><span class="badge badge-light text-uppercase mb-1">${escapeHtml(role)}</span><div>${escapeHtml(m.content)}</div></div>`
      );
    });
  }
  return `<div class="p-3 bg-light">${parts.join('')}</div>`;
}

export default function ConversationsDataTable({
  refreshToken,
  onError,
  onSelectionChange,
  clearSelectionToken,
  minMessages,
}) {
  const assetsReady = useDataTablesAssets();
  const tableRef = useRef(null);
  const dtRef = useRef(null);
  const messageCacheRef = useRef({});
  // PDF export selection: ids picked by the user + every row we've loaded, so the
  // parent receives full session objects (not just ids) to build transcripts with.
  const selectedRef = useRef(new Set());
  const rowsByIdRef = useRef({});
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  // DataTables' ajax closure is created once; read the live filter via a ref.
  const minMessagesRef = useRef(minMessages);
  minMessagesRef.current = minMessages;

  const emitSelection = useCallback(() => {
    const rows = Array.from(selectedRef.current)
      .map((id) => rowsByIdRef.current[id])
      .filter(Boolean);
    onSelectionChangeRef.current?.(rows);
  }, []);
  const [meta, setMeta] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    loading: true,
  });

  const fetchMessages = useCallback(async (sessionId) => {
    if (messageCacheRef.current[sessionId]) {
      return messageCacheRef.current[sessionId];
    }
    const res = await fetch(
      `${getApiBase()}/api/dashboard/session-messages?sessionId=${encodeURIComponent(sessionId)}`,
      { headers: getDashboardAuthHeaders() }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
    const msgs = Array.isArray(data.messages) ? data.messages : [];
    messageCacheRef.current[sessionId] = msgs;
    return msgs;
  }, []);

  useEffect(() => {
    messageCacheRef.current = {};
  }, [refreshToken]);

  useEffect(() => {
    const $ = getJQuery();
    if (!assetsReady || !$ || !tableRef.current) return undefined;

    const $table = $(tableRef.current);

    if ($.fn.DataTable.isDataTable(tableRef.current)) {
      $table.DataTable().clear().destroy();
    }

    const dt = $table.DataTable({
      processing: true,
      serverSide: true,
      responsive: true,
      autoWidth: false,
      pageLength: 20,
      lengthMenu: [10, 20, 50, 100],
      order: [[3, 'desc']],
      paging: true,
      dom:
        '<"row mb-2"<"col-sm-6"l><"col-sm-6"f>>' +
        '<"row"<"col-sm-12"tr>>',
      language: {
        emptyTable: 'No conversations yet. Complete a session on the avatar page to save one.',
        processing: '<i class="fas fa-spinner fa-spin"></i> Loading…',
      },
      ajax: (dtParams, callback) => {
        setMeta((m) => ({ ...m, loading: true }));
        const page = Math.floor(dtParams.start / dtParams.length) + 1;
        const pageSize = dtParams.length;
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(pageSize),
          includeMessages: 'true',
        });
        if (minMessagesRef.current != null) {
          params.set('minMessages', String(minMessagesRef.current));
        }

        fetch(`${getApiBase()}/api/dashboard/sessions?${params}`, {
          headers: getDashboardAuthHeaders(),
        })
          .then((res) => res.json().then((json) => ({ res, json })))
          .then(({ res, json }) => {
            if (!res.ok) {
              onError(json.error || `Error ${res.status}`);
              setMeta({ page: 1, pageSize: 20, total: 0, totalPages: 0, loading: false });
              callback({
                draw: dtParams.draw,
                recordsTotal: 0,
                recordsFiltered: 0,
                data: [],
              });
              return;
            }
            const rows = (Array.isArray(json.sessions) ? json.sessions : []).map((s) =>
              enrichSessionWithCost(s)
            );
            // Remember every loaded row so a selection can be exported later.
            rows.forEach((r) => {
              if (r?.sessionId) rowsByIdRef.current[r.sessionId] = r;
            });
            const total = json.total ?? rows.length;
            const totalPages = json.totalPages ?? Math.max(1, Math.ceil(total / pageSize));
            setMeta({ page, pageSize, total, totalPages, loading: false });
            callback({
              draw: dtParams.draw,
              recordsTotal: total,
              recordsFiltered: total,
              data: rows,
            });
          })
          .catch((err) => {
            onError(err?.message || 'Failed to load conversations');
            setMeta({ page: 1, pageSize: 20, total: 0, totalPages: 0, loading: false });
            callback({
              draw: dtParams.draw,
              recordsTotal: 0,
              recordsFiltered: 0,
              data: [],
            });
          });
      },
      columns: [
        {
          data: null,
          title: '',
          orderable: false,
          className: 'text-center select-col',
          render: (d, t, row) =>
            `<input type="checkbox" class="row-select" data-sid="${escapeHtml(row.sessionId)}"${
              selectedRef.current.has(row.sessionId) ? ' checked' : ''
            } aria-label="Select conversation for PDF export" />`,
        },
        {
          data: 'userEmail',
          title: 'Email',
          render: (email, type, row) => {
            const label = escapeHtml(email || row.avatarName || 'Unknown user');
            const badge = row.endedAt
              ? ''
              : ' <span class="badge badge-warning ml-1">In progress</span>';
            return `<strong>${label}</strong>${badge}`;
          },
        },
        {
          data: 'sessionId',
          title: 'Session ID',
          className: 'text-muted small',
          render: (sid) => escapeHtml(sid || '—'),
        },
        {
          data: 'startedAt',
          title: 'Started',
          render: (iso, type, row) => {
            let html = formatDate(iso);
            if (row.endedAt) {
              html += `<br><span class="text-muted small">→ ${formatDate(row.endedAt)}</span>`;
            }
            return html;
          },
        },
        {
          data: 'sessionCostUsd',
          title: 'Cost',
          className: 'text-success font-weight-bold',
          render: (cost) => formatCostUsd(cost),
        },
        {
          data: 'messageCount',
          title: 'Msgs',
          className: 'text-center',
          render: (n) => n ?? 0,
        },
        {
          data: null,
          title: '',
          orderable: false,
          className: 'text-center',
          defaultContent: '<i class="fas fa-chevron-down text-muted"></i>',
        },
      ],
      rowCallback(row, data) {
        $(row).addClass('session-toggle').css('cursor', 'pointer');
        $(row).attr('data-session-id', data.sessionId);
      },
    });

    dtRef.current = dt;

    // Row checkboxes: toggle selection without expanding the transcript.
    $table.on('click', 'input.row-select', function onSelectClick(e) {
      e.stopPropagation();
    });
    $table.on('change', 'input.row-select', function onSelectChange(e) {
      e.stopPropagation();
      const sid = String($(this).attr('data-sid') || '');
      if (!sid) return;
      if (this.checked) selectedRef.current.add(sid);
      else selectedRef.current.delete(sid);
      emitSelection();
    });

    // Header checkbox selects/clears every row on the current page.
    $table.on('click', 'input.select-all-rows', function onSelectAllClick(e) {
      e.stopPropagation();
    });
    $table.on('change', 'input.select-all-rows', function onSelectAll() {
      const checkAll = this.checked;
      $table.find('tbody input.row-select').each(function applyEach() {
        const sid = String($(this).attr('data-sid') || '');
        if (!sid) return;
        this.checked = checkAll;
        if (checkAll) selectedRef.current.add(sid);
        else selectedRef.current.delete(sid);
      });
      emitSelection();
    });

    $table.on('click', 'tbody tr', async function onRowClick() {
      const row = dt.row(this);
      const data = row.data();
      if (!data?.sessionId) return;

      if (row.child.isShown()) {
        row.child.hide();
        $(this).find('i.fas').removeClass('fa-chevron-up').addClass('fa-chevron-down');
        return;
      }

      dt.rows().every(function hideOthers() {
        if (this.child.isShown()) {
          this.child.hide();
          $(this.node()).find('i.fas').removeClass('fa-chevron-up').addClass('fa-chevron-down');
        }
      });

      $(this).find('i.fas').removeClass('fa-chevron-down').addClass('fa-chevron-up');

      const preloaded = normalizeDashboardMessages(data.messages);
      if (preloaded.length > 0) {
        row.child(buildDetailHtml(data, preloaded, false)).show();
        return;
      }

      row.child(buildDetailHtml(data, null, true)).show();

      try {
        const messages = await fetchMessages(data.sessionId);
        row.child(buildDetailHtml(data, messages, false)).show();
      } catch (err) {
        row.child(
          `<div class="p-3 bg-light text-danger text-sm">${escapeHtml(err.message || 'Failed to load messages')}</div>`
        ).show();
      }
    });

    return () => {
      $table.off('click', 'tbody tr');
      $table.off('click', 'input.row-select');
      $table.off('change', 'input.row-select');
      $table.off('click', 'input.select-all-rows');
      $table.off('change', 'input.select-all-rows');
      if ($.fn.DataTable.isDataTable(tableRef.current)) {
        $table.DataTable().clear().destroy();
      }
      dtRef.current = null;
    };
  }, [assetsReady, refreshToken, fetchMessages, onError, emitSelection]);

  // Parent cleared the selection (e.g. after an export or the Clear button).
  // Empty the set, untick what's on screen, then redraw so DataTables regenerates
  // every checkbox from the now-empty selection (covers rows the DOM query misses,
  // e.g. cells collapsed by the responsive extension or on other pages).
  useEffect(() => {
    if (!clearSelectionToken) return;
    selectedRef.current.clear();
    const $ = getJQuery();
    if ($ && tableRef.current) {
      $(tableRef.current).find('input.row-select, input.select-all-rows').prop('checked', false);
    }
    if (dtRef.current) {
      try {
        dtRef.current.rows().invalidate().draw(false);
      } catch (_) {}
    }
  }, [clearSelectionToken]);

  // Filter changed — reload from the server starting at page 1.
  useEffect(() => {
    if (!dtRef.current) return;
    try {
      dtRef.current.page(0).draw(false);
    } catch (_) {}
  }, [minMessages]);

  const handlePageChange = (p) => {
    const dt = dtRef.current;
    if (!dt) return;
    dt.page(p - 1).draw('page');
  };

  const from = meta.total === 0 ? 0 : (meta.page - 1) * meta.pageSize + 1;
  const to = Math.min(meta.page * meta.pageSize, meta.total);

  if (!assetsReady) {
    return (
      <p className="text-center text-muted py-5 mb-0">
        <i className="fas fa-spinner fa-spin mr-2" />
        Loading table…
      </p>
    );
  }

  return (
    <>
      <table
        ref={tableRef}
        id="conversations-datatable"
        className="table table-bordered table-striped table-hover w-100 mb-0"
      >
        <thead>
          <tr>
            <th className="text-center" style={{ width: 34 }}>
              <input
                type="checkbox"
                className="select-all-rows"
                aria-label="Select all conversations on this page"
              />
            </th>
            <th>Email</th>
            <th>Session ID</th>
            <th>Started</th>
            <th>Cost</th>
            <th>Msgs</th>
            <th />
          </tr>
        </thead>
        <tbody />
      </table>

      <div className="conversations-table-footer clearfix border-top bg-white px-2 py-2">
        <p className="text-muted small mb-2 mb-md-0 float-md-left pt-1">
          {meta.total > 0
            ? `Showing ${from} to ${to} of ${meta.total} entries`
            : 'No entries to show'}
        </p>
        <PaginationBar
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          pageSize={meta.pageSize}
          disabled={meta.loading}
          onPageChange={handlePageChange}
        />
      </div>
    </>
  );
}
